// Angular Core y Formularios
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  FormArray,
  FormControl,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';

// Firebase SDK (Funciones, Storage y Firestore)
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadString, getDownloadURL } from 'firebase/storage';
import { Firestore, doc, setDoc } from '@angular/fire/firestore';
import { Timestamp } from 'firebase/firestore';

// Servicios personalizados del proyecto
import { DataService } from '../../services/data';
import { SpinnerService } from '../../services/spinner.service';
import { ToastService } from '../../services/toast.service';
import { ImageService } from '../../services/image.service';

// Ruteo
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

// PrimeNG - UI Components
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputTextModule } from 'primeng/inputtext';
import { TimelineModule } from 'primeng/timeline';
import { TabsModule } from 'primeng/tabs';
import { AvatarModule } from 'primeng/avatar';
import { MenubarModule } from 'primeng/menubar';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { TooltipModule } from 'primeng/tooltip';
import { TextareaModule } from 'primeng/textarea';
import { PrimeNG } from 'primeng/config';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MissionMoneyPipe } from '../../pipes/mission-money.pipe';


// Iconos y utilidades visuales
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

// Librerías externas
import { NgxImageCompressService } from 'ngx-image-compress';

// RXJS - Utilidades
import { firstValueFrom, Subscription, take } from 'rxjs';
import { Member } from '../../models/member.model';
import { resolveLoggedInDisplayName } from '../../helpers/user-display-name.helper';
import { FamiliesService } from '../../services/familes.service';
import { MemberService } from '../../services/member.service';
import { ReportePdfService } from '../../services/reporte-pdf.service';
import { LanguageService } from '../../services/language.service';
import {
  HISTORIAL_UI_INITIAL,
  historialHasMore,
  nextHistorialVisibleCount,
} from '../../utils/historial.util';
import { CanDeactivateWithUnsavedChanges } from '../../guards/unsaved-changes.guard';

type FamilyRole = 'PADRE' | 'MADRE' | 'HIJO';

interface HistorialCambioView {
  fecha: string;
  byName: string;
  action: 'create' | 'update';
  resumen?: string;
  campos?: string[];
}

@Component({
  selector: 'app-member-detail',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, RouterLink, RadioButtonModule, InputTextModule, CommonModule, AutoCompleteModule, TooltipModule, DialogModule, ToastModule, MenubarModule, ButtonModule, FloatLabelModule, DatePickerModule, FontAwesomeModule, SelectModule, TextareaModule, CheckboxModule, TimelineModule, TabsModule, AvatarModule, ConfirmDialogModule, TableModule, TranslateModule, MissionMoneyPipe],
  templateUrl: './member-detail.html',
  styleUrl: './member-detail.css'
})

export class MemberDetail implements OnInit, OnDestroy, CanDeactivateWithUnsavedChanges {
  private memberId: string | null = null;
  notasPastorales: { fecha: Date; autor: string; contenido: string }[] = [];
  memberDetailForm: FormGroup;
  private actualizarMiembroCallable: (data: any) => Promise<any>;
  private gestionarFamiliaCallable: (data: any) => Promise<any>;
  private crearMiembroCallable: (data: any) => Promise<any>;

  estadoCivilOptions: any[] = [];

  generoOptions: any[] = [];

  tipoDocumentoOptions: any[] = [];

  private catalogosSubscription: any;
  private langSubscription: any;
  private rawTipoDocumentoOptions: any[] = [];

  camposClaveMiembro = [
    'nombres',
    'apellidos',
    'photoUrl',
    'tipo',
    'estadoParticipacion',
    'estadoEspiritual',
    'areaServicio',
    'telefono',
    'email',
    'direccion',
    'estadoCivil',
    'conyugeId',
    'conyugeNombre',
    'region',
    'ciudad',
    'genero',
    'pais',
    'paisNacimiento',
    'nacionalidad',
    'tipoDocumento',
    'numeroDocumento',
    'fechaNacimiento',
    'fechaBautismo',
    'fechaIngreso',
    'bautismoEspirituSanto',
    'ministerioActivo',
    'departamentoEdad',
    'familiaId',
    'salud',
    'tipoSangre',
    'alergias',
    'enfermedades',
    'hijos' // cuidado: debe estar normalizado (misma estructura y orden)
  ];

  nivelesEstudios = [
    { label: 'Primaria', value: 'Primaria' },
    { label: 'Secundaria', value: 'Secundaria' },
    { label: 'Técnico', value: 'Técnico' },
    { label: 'Tecnólogo', value: 'Tecnólogo' },
    { label: 'Universitario', value: 'Universitario' },
    { label: 'Postgrado', value: 'Postgrado' },
    { label: 'Otro', value: 'Otro' }
  ];

  nivelBautismo = [
    { label: 'Bautizado', value: 'Bautizado' },
    { label: 'No Bautizado', value: 'No Bautizado' },
    { label: 'En Proceso', value: 'En Proceso' }
  ];

  bautizadoEspirituSanto = [
    { label: 'Sí', value: 'Sí' },
    { label: 'No', value: 'No' }
  ];

  estadoParticipacion = [
    { label: 'Activo', value: 'Activo' },
    { label: 'Inactivo', value: 'Inactivo' },
    { label: 'No Reconocido', value: 'No Reconocido' },
    { label: 'En Disciplina', value: 'En Disciplina' }
  ];

  tipoPersona = [
    { label: 'Miembro', value: 'Miembro' },
    { label: 'Visitante', value: 'Visitante' },
    { label: 'Nuevo Creyente', value: 'Nuevo Creyente' }
  ];

  tiposSangre: { label: string; value: string }[] = [
    { label: 'A+', value: 'A+' },
    { label: 'A-', value: 'A-' },
    { label: 'B+', value: 'B+' },
    { label: 'B-', value: 'B-' },
    { label: 'AB+', value: 'AB+' },
    { label: 'AB-', value: 'AB-' },
    { label: 'O+', value: 'O+' },
    { label: 'O-', value: 'O-' }
  ];


  camposFamiliares = ['conyugeId', 'conyugeNombre', 'hijos', 'familiaId'];

  ministeriosTexto: any;
  rolesTexto: any;
  conyugeNombre?: string;
  conyugeId?: string;
  activeTabIndex: any;
  photo?: string;
  churchId: string = '';
  usuarioUid: string | null | undefined = '';

  activeView: string = 'personal';
  menuItems: any[] = [];

  setActiveView(view: string): void {
    this.activeView = view;
    if (view === 'diezmos') {
      this.loadDiezmoSummaries();
    }
  }
  mostrarConyuge: boolean = false;
  cantidadHijos: number = 0;
  tieneHijos: boolean = false;
  tieneConyugeBtn: boolean = false;
  conyugeGenero: string = '';
  memberApellido: string = '';
  conyugeApellido: string = '';
  opcionesConyuge: any[] = [];
  opcionesHijo: any[][] = [];
  fechaNacimientoConyuge: Date | undefined | Timestamp = undefined;
  generoConyuge: string = '';

  private todosLosMiembros: any[] = [];
  private todasLasFamilias: any[] = [];
  private originalMemberData: any;
  dataFamiliaOriginal: any;
  dataFamiliaActual: any;

  edadMiembro: string = '';
  antiguedadIglesia: string = '';

  mostrarModalEstudio: boolean = false;
  estudiosBiblicos: string[] = [];
  nuevoEstudioTexto: string = '';

  mostrarModalSalud: boolean = false;
  estadosDeSalud: string[] = [];
  nuevoEstadoSalud: string = '';

  nuevoContenidoNota: string = '';
  mostrarModalNota: boolean = false;

  usuarioNombre: string = '';
  private usuarioEmail: string = '';
  private membersSubscription?: Subscription;
  private familiesSubscription?: Subscription;

  previewFotoBase64: string | null = null;   // para ver en el avatar
  fotoPendienteBase64: string | null = null; // para subir después

  creando: boolean = false;
  areaServicio: string = '';

  selectedConyuge: Member | null = null;
  actualizarConyuge: boolean = false;
  actualizarHijos: boolean = false;
  selectedHijos: Member[] = [];

  miembro: Member | null = null;
  familiaActual: any;

  // Historial de cambios (auditoría embebida) — solo Pastor/Admin
  public canViewLogs = false;
  public historialDialogVisible = false;
  public historialEntries: HistorialCambioView[] = [];
  public historialVisibleCount = HISTORIAL_UI_INITIAL;

  updatePhoto: boolean = false;

  // DIEZMOS
  diezmoSummaries: any[] = [];
  diezmoDetails: any[] = [];
  selectedYearDiezmos: string | null = null;
  loadingDiezmos: boolean = false;

  constructor(
    private fb: FormBuilder,
    private confirmationService: ConfirmationService,
    private firestore: Firestore,
    private imageCompress: NgxImageCompressService,
    private dataService: DataService,
    private route: ActivatedRoute,
    private router: Router,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
    private imageService: ImageService,
    private primengConfig: PrimeNG,
    private familyActions: FamiliesService,
    private memberService: MemberService,
    private reportePdfService: ReportePdfService,
    private translate: TranslateService,
    private languageService: LanguageService
  ) {
    const functions = getFunctions();
    this.actualizarMiembroCallable = httpsCallable(functions, 'actualizarMiembro');
    this.gestionarFamiliaCallable = httpsCallable(functions, 'gestionarFamilia');
    this.crearMiembroCallable = httpsCallable(functions, 'crearMiembro');
    this.memberDetailForm = this.fb.group({
      nombres: ['', Validators.required],
      apellidos: [''],
      photoUrl: [''],
      tipo: ['Visitante'],
      estadoParticipacion: [''],
      bautismo: [''],
      bautismoEspirituSanto: [''],
      telefono: [''],
      email: ['', Validators.email],
      direccion: [''],
      ciudad: [''],
      region: [''],
      tipoDocumento: [''],
      numeroDocumento: [''],
      paisNacimiento: [''],
      nacionalidad: [''],
      estadoCivil: [''],
      fechaNacimiento: [null],
      fechaBautismo: [null],
      fechaIngreso: [null],
      ministerioActivo: [false],
      departamentoEdad: [''],
      ministerios: [[]],
      roles: this.fb.control([]),
      fechaCreacion: [null],
      fechaActualizacion: [null],
      activo: [true],
      tieneConyuge: [null as 'SI' | 'NO' | null],
      conyugeId: [null],
      conyugeNombre: [''],
      estadosDeSalud: [''],
      tipoSangre: [''],
      alergias: [''],
      enfermedades: [''],
      tieneHijos: [false],
      hijos: this.fb.array([]),
      notasPastorales: this.fb.array([] as FormGroup[]),
      familiaId: [''],
      genero: ['', Validators.required],
      pais: [''],
      rolGeneral: [''],
      idDocMember: [''],
      estudios: [''],
      profesion: [''],
      areaServicio: [''],
      estudiosBiblicos: ['']
    });
  }

  loadDiezmoSummaries() {
    if (!this.memberId || !this.churchId) return;
    this.loadingDiezmos = true;
    this.memberService.getDiezmoSummaries(this.churchId, this.memberId).subscribe({
      next: (data) => {
        this.diezmoSummaries = data;
        this.loadingDiezmos = false;
      },
      error: (err) => {
        this.loadingDiezmos = false;
        this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_TITHES_SUMMARY'));
      }
    });
  }

  verDetalleDiezmos(year: string) {
    if (this.selectedYearDiezmos === year) {
      this.selectedYearDiezmos = null; // Toggle off
      this.diezmoDetails = [];
      return;
    }
    this.selectedYearDiezmos = year;
    this.loadingDiezmos = true;
    this.memberService.getDiezmosByYear(this.churchId, this.memberId!, year).subscribe({
      next: (data) => {
        this.diezmoDetails = data;
        this.loadingDiezmos = false;
      },
      error: (err) => {
        this.loadingDiezmos = false;
        this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_TITHES_DETAIL'));
      }
    });
  }

  async generarCertificado(summary: any) {
    if (!this.memberId || !this.churchId) return;

    this.spinnerService.show();
    try {
      // Necesitamos los detalles para el certificado en base al año seleccionado
      const detalles = await firstValueFrom(this.memberService.getDiezmosByYear(this.churchId, this.memberId, summary.year));

      // Datos del miembro (usamos el valor del formulario para tener los datos más recientes/editados)
      const miembro = this.memberDetailForm.getRawValue();
      const iglesia: any = this.dataService.dataChurch$ ? await firstValueFrom(this.dataService.dataChurch$.pipe(take(1))) : null;

      const data = {
        anio: summary.year,
        // TODO: Obtener responsable y cargo de configuración o usuario actual
        responsable: '..........................................................',
        cargo: this.translate.instant('MEMBER_DETAIL.CERT_ROLE_TREASURER'),
        iglesiaNombre: iglesia?.nombre || '',
        iglesiaNif: iglesia?.settings?.cif || '',
        iglesiaDireccion: iglesia?.direccion || '',
        miembroNombre: `${miembro.nombres} ${miembro.apellidos}`,
        miembroNif: miembro.numeroDocumento,
        miembroDireccion: miembro.direccion,
        donaciones: detalles,
        total: summary.total || detalles.reduce((acc: number, curr: any) => acc + (curr.monto || 0), 0),
        ciudadFirma: miembro.ciudad || ''
      };

      await this.reportePdfService.generarCertificadoDonaciones(data);

    } catch (err) {
      this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_CERT_GEN'));
    } finally {
      this.spinnerService.hide();
    }
  }

  ngOnInit(): void {
    this.buildTranslatedOptions();
    this.applyPrimeNGDatepickerLocale();
    this.langSubscription = this.translate.onLangChange.subscribe(() => {
      this.buildTranslatedOptions();
      this.buildTipoDocumentoOptions();
      this.applyPrimeNGDatepickerLocale();
    });
    this.catalogosSubscription = this.dataService.catalogosConfig$.subscribe(config => {
      if (config && config.tipoDocumento && config.tipoDocumento.length > 0) {
        this.rawTipoDocumentoOptions = config.tipoDocumento
          .filter((o: any) => o.activo)
          .sort((a: any, b: any) => a.orden - b.orden);
        this.buildTipoDocumentoOptions();
      }
    });

    this.membersSubscription = this.dataService.members$.subscribe(members => {
      this.todosLosMiembros = members;
    });

    this.dataService.userRoles$.subscribe(roles => {
      this.canViewLogs = (roles ?? []).some((role: string) => ['Pastor', 'Admin'].includes(role));
    });

    this.dataService.displayName$.subscribe(dataUser => {
      this.usuarioNombre = dataUser;
    });

    this.dataService.dataCurrentUser$.subscribe(userData => {
      const user = Array.isArray(userData) ? userData[0] : userData;
      this.usuarioEmail = (user?.email || '').trim();
      const resolved = resolveLoggedInDisplayName({
        user,
        authDisplayName: this.usuarioNombre,
        email: this.usuarioEmail,
      });
      if (resolved) {
        this.usuarioNombre = resolved;
      }
    });

    this.familiesSubscription = this.dataService.families$.subscribe(families => {
      this.todasLasFamilias = families;
    });

    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      this.resetComponentState();
      this.memberId = id;
      if (this.memberId) {
        this.loadMemberData(this.memberId);
      } else {
        this.creando = true;
      }
    });

  }

  leerFamilia(familiaId: string) {
    return this.todasLasFamilias
      .filter(m => m.familiaId === familiaId)
      .map(member => ({
        ...member,
        nombreCompleto: `${member.nombres ?? ''} ${member.apellidos ?? ''}`.trim(),
        id: member.id
      }));
  }

  async loadMemberData(memberId: string): Promise<void> {
    const church = this.dataService.getChurchId();
    if (!church) {
      this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_CHURCH_ID'));
      this.router.navigate(['/dashboard/members']);
      return;
    }

    this.churchId = church;
    this.spinnerService.show();

    try {
      // 1) Tomar el snapshot actual del cache local
      const members = this.dataService.getCurrentMembers() ?? this.todosLosMiembros ?? [];
      this.todosLosMiembros = members;

      // 2) Buscar por id (ajusta según tu modelo real)
      const member = members?.find((m: any) =>
        (m.id ?? m.memberId) === memberId
      );

      if (!member) {
        this.toastService.showWarning(this.translate.instant('MEMBER_DETAIL.TOAST_WARN_REDIRECT_MEMBERS'));
        this.spinnerService.hide();
        this.router.navigate(['/dashboard/members']);
        return;
      }

      // 3) Reusar la lógica para poblar el formulario
      this.miembro = member;
      this.aplicarMiembroAlFormulario(member);

    } catch (err) {
      this.spinnerService.hide();
      this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_LOAD_DETAIL'));
      this.router.navigate(['/dashboard/members']);
    }
  }

  /**
   * Abre el diálogo con el historial de cambios embebido del miembro.
   * Solo accesible para Pastor/Admin (gobernado por canViewLogs en la plantilla).
   * No realiza lecturas extra: usa los datos ya cargados del miembro.
   */
  openHistorial(): void {
    const source: any = this.miembro || this.originalMemberData;
    const entries: any[] = Array.isArray(source?.historialCambios) ? source.historialCambios : [];

    this.historialEntries = entries
      .map((entry) => ({
        fecha: this.formatHistorialFecha(entry?.at),
        byName: entry?.byName || source?.updatedByName || source?.createdByName || '—',
        action: entry?.action === 'create' ? 'create' : 'update',
        resumen: entry?.resumen || undefined,
        campos: Array.isArray(entry?.campos) ? entry.campos : undefined,
      } as HistorialCambioView))
      .reverse();

    this.historialVisibleCount = HISTORIAL_UI_INITIAL;
    this.historialDialogVisible = true;
  }

  get visibleHistorialEntries(): HistorialCambioView[] {
    return this.historialEntries.slice(0, this.historialVisibleCount);
  }

  get hasMoreHistorialEntries(): boolean {
    return historialHasMore(this.historialVisibleCount, this.historialEntries.length);
  }

  loadMoreHistorial(): void {
    this.historialVisibleCount = nextHistorialVisibleCount(
      this.historialVisibleCount,
      this.historialEntries.length
    );
  }

  closeHistorial(): void {
    this.historialDialogVisible = false;
    this.historialEntries = [];
    this.historialVisibleCount = HISTORIAL_UI_INITIAL;
  }

  get historialCreatedByName(): string {
    const source: any = this.miembro || this.originalMemberData;
    return source?.createdByName || '—';
  }

  get hasUnsavedChanges(): boolean {
    return (
      this.memberDetailForm.dirty ||
      this.updatePhoto ||
      this.actualizarConyuge ||
      this.actualizarHijos
    );
  }

  get saveButtonStyleClass(): string {
    const classes = ['member-detail-hero__btn', 'member-detail-hero__btn--save'];
    if (this.hasUnsavedChanges) {
      classes.push('member-detail-hero__btn--dirty');
    }
    return classes.join(' ');
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedChanges) {
      return;
    }
    event.preventDefault();
    event.returnValue = '';
  }

  confirmDeactivate(): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        message: this.translate.instant('MEMBER_DETAIL.UNSAVED_LEAVE_MESSAGE'),
        header: this.translate.instant('MEMBER_DETAIL.UNSAVED_LEAVE_HEADER'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translate.instant('MEMBER_DETAIL.UNSAVED_LEAVE_ACCEPT'),
        rejectLabel: this.translate.instant('COMMON.CANCEL'),
        acceptButtonStyleClass: 'p-button-danger',
        rejectButtonStyleClass: 'p-button-secondary',
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  get historialUpdatedByName(): string {
    const source: any = this.miembro || this.originalMemberData;
    return source?.updatedByName || source?.createdByName || '—';
  }

  private formatHistorialFecha(value: any): string {
    if (!value) return '—';
    let date: Date | null = null;
    if (typeof value?.toDate === 'function') {
      date = value.toDate();
    } else if (typeof value?.seconds === 'number') {
      date = new Date(value.seconds * 1000);
    } else if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) date = parsed;
    }
    if (!date) return '—';
    const lang = this.translate.getCurrentLang() || 'es';
    const locale = lang === 'it' ? 'it-IT' : 'es-ES';
    return date.toLocaleString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  resetComponentState(): void {
    this.memberDetailForm.reset({
      // Valores por defecto para que el formulario no quede inválido
      nombres: '',
      apellidos: '',
      tipo: '',
      estadoParticipacion: '',
      bautismo: '',
      estadoCivil: '',
      tieneConyuge: null,
      tieneHijos: false,
      hijos: [],
      notasPastorales: []
    });

    // Limpiar FormArrays explícitamente
    this.hijosFormArray.clear();
    this.notasPastoralesForm.clear();

    // Resetear todas las propiedades del componente
    this.memberId = null;
    this.originalMemberData = null;
    this.dataFamiliaOriginal = null;
    this.usuarioUid = null;
    this.activeView = 'personal';
    this.edadMiembro = '';
    this.antiguedadIglesia = '';
    this.opcionesConyuge = [];
    this.opcionesHijo = [];
    this.estudiosBiblicos = [];
    this.estadosDeSalud = [];
    this.mostrarConyuge = false;
    this.diezmoSummaries = [];
    this.diezmoDetails = [];
    this.selectedYearDiezmos = null;
  }

  private applyPrimeNGDatepickerLocale(): void {
    const lang = this.translate.getCurrentLang() || 'es';
    if (lang === 'it') {
      this.primengConfig.setTranslation({
        firstDayOfWeek: 1,
        dayNames: ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'],
        dayNamesShort: ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'],
        dayNamesMin: ['D', 'L', 'M', 'M', 'G', 'V', 'S'],
        monthNames: ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'],
        monthNamesShort: ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'],
        today: 'Oggi',
        clear: 'Cancella',
        dateFormat: 'dd/mm/yy',
        weekHeader: 'Sm'
      });
    } else {
      this.primengConfig.setTranslation({
        firstDayOfWeek: 1,
        dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
        dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
        dayNamesMin: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
        monthNames: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
        monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
        today: 'Hoy',
        clear: 'Limpiar',
        dateFormat: 'dd/mm/yy',
        weekHeader: 'Sm'
      });
    }
  }

  get notasPastoralesForm(): FormArray {
    return this.memberDetailForm.get('notasPastorales') as FormArray;
  }

  construirDataFamilia(data: any): any {
    const datosFamiliaresOriginales = {
      conyugeId: data.conyugeId ?? null,
      conyugeNombre: data.conyugeNombre ?? '',
      hijos: (data.hijos ?? []).map((hijo: any) => {
        // <<<<<< INICIO DEL CAMBIO >>>>>>
        // Convertir fechaNacimiento de Timestamp a Date si es necesario
        // Esto asegura que tanto el objeto "original" como el "actual" tengan Date
        const fechaNacimientoNormalizada = hijo.fechaNacimiento && typeof (hijo.fechaNacimiento as any).toDate === 'function'
          ? (hijo.fechaNacimiento as any).toDate()
          : hijo.fechaNacimiento; // Si ya es Date o null, se mantiene
        // <<<<<< FIN DEL CAMBIO >>>>>>

        return {
          id: hijo.id || null,
          nombre: hijo.nombre || '',
          genero: hijo.genero || null,
          fechaNacimiento: fechaNacimientoNormalizada || null // Usa la fecha normalizada
        };
      }),
      familiaId: data.familiaId ?? null,
    };

    return datosFamiliaresOriginales;
  }

  setHijos(hijos: any[]) {
    const hijosArray = this.hijosFormArray; // Usa el getter para el FormArray
    hijosArray.clear(); // Limpiar controles existentes
    this.opcionesHijo = []; // Limpiar las opciones de autocompletado también

    if (hijos && hijos.length > 0) {
      this.memberDetailForm.get('tieneHijos')?.setValue(true, { emitEvent: false }); // Establecer el radio button a 'Sí'
      hijos.forEach(hijo => {
        // Convertir fechaNacimiento de Timestamp a Date si es necesario
        const fechaNacimiento = hijo.fechaNacimiento && typeof (hijo.fechaNacimiento as any).toDate === 'function'
          ? (hijo.fechaNacimiento as any).toDate()
          : hijo.fechaNacimiento;

        hijosArray.push(this.fb.group({
          nombre: [hijo.nombre], // Este será el nombre (manual o nombre completo del miembro seleccionado)
          id: [hijo.id || null],
          fechaNacimiento: [fechaNacimiento || null],
          genero: [hijo.genero || null]
        }));
        this.opcionesHijo.push([]); // Inicializar array de opciones para cada hijo
      });
    } else {
      this.memberDetailForm.get('tieneHijos')?.setValue(false); // Establecer el radio button a 'No'
    }
  }

  setNotasPastorales(notas: { fecha: Date | Timestamp; autor: string; contenido: string }[]) {
    const formArray = new FormArray<FormGroup>(
      (Array.isArray(notas) ? notas : [])
        .filter(nota => nota && String(nota.contenido ?? '').trim().length > 0)
        .map(nota => new FormGroup({
          fecha: new FormControl(this.toDateOrNull(nota.fecha)),
          autor: new FormControl<string>(String(nota.autor ?? '').trim()),
          contenido: new FormControl<string>(String(nota.contenido).trim())
        }))
    );

    this.memberDetailForm.setControl('notasPastorales', formArray);
  }

  formatNotaFecha(value: unknown): string {
    const date = this.toDateOrNull(value);
    if (!date) return '—';
    return this.formatHistorialFecha(date);
  }

  private toDateOrNull(value: unknown): Date | null {
    if (!value) return null;
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    if (typeof (value as any)?.toDate === 'function') {
      const fromTs = (value as Timestamp).toDate();
      return isNaN(fromTs.getTime()) ? null : fromTs;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    const raw = value as { seconds?: number; _seconds?: number; nanoseconds?: number; _nanoseconds?: number };
    const seconds = raw.seconds ?? raw._seconds;
    if (typeof seconds === 'number') {
      const nanos = raw.nanoseconds ?? raw._nanoseconds ?? 0;
      const parsed = new Date(seconds * 1000 + nanos / 1e6);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  }

  agregarNotaPastoral() {
    this.notasPastoralesForm.push(this.fb.group({
      fecha: [new Date()],
      autor: [''],
      contenido: ['']
    }));
  }

  eliminarNotaPastoral(index: number) {
    this.notasPastoralesForm.removeAt(index);
  }

  resetForm(): void {
    this.memberDetailForm.reset();
  }

  async onSubmit(): Promise<void> {
    if (!this.memberDetailForm.valid) {
      this.memberDetailForm.markAllAsTouched();
      this.toastService.showWarning(this.translate.instant('MEMBER_DETAIL.TOAST_WARN_REQUIRED'));
      return;
    }

    this.spinnerService.show();

    const churchId = this.dataService.getChurchId();

    if (!churchId) {
      this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_NO_CHURCH'));
      this.spinnerService.hide();
      return;
    }

    // Obtiene los datos CRUDOS del formulario, incluyendo el cónyuge y los hijos seleccionados
    const memberData = this.memberDetailForm.getRawValue();
    if (this.memberId && !this.creando) {
      if (!memberData.conyugeId) {
        memberData.conyugeNombre = '';
      }

      const email = this.memberDetailForm.get('email')?.value;
      const verificarEmail = await this.verificarEmailMiembro(email, { excludeMemberId: this.memberId })
      if (!verificarEmail) {
        this.spinnerService.hide();
        this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_EMAIL_DUPLICATE'));
        return;
      }

      // Normalizar hijos, asegurando que los datos son consistentes
      const tieneHijos = this.memberDetailForm.get('tieneHijos')?.value;
      memberData.hijos = tieneHijos
        ? (memberData.hijos || [])
          .filter((hijo: any) => (hijo.nombre && (typeof hijo.nombre === 'string' ? hijo.nombre.trim() : hijo.nombre.id)))
          .map((hijo: any) => {
            const esNombreObjeto = typeof hijo.nombre === 'object' && hijo.nombre !== null;
            const id = hijo.id || (esNombreObjeto ? hijo.nombre.id : null);
            const nombre = esNombreObjeto ? hijo.nombre.nombreCompleto : hijo.nombre;
            const genero = hijo.genero || (esNombreObjeto ? hijo.nombre.genero : null);
            let fechaNacimiento = hijo.fechaNacimiento || (esNombreObjeto ? hijo.nombre.fechaNacimiento : null);

            if (fechaNacimiento && typeof fechaNacimiento.toDate === 'function') {
              fechaNacimiento = fechaNacimiento.toDate();
            }

            return { id, nombre, genero, fechaNacimiento: fechaNacimiento || null };
          })
        : [];

      try {
        // 1. ACTUALIZAR EL DOCUMENTO DEL MIEMBRO PRINCIPAL
        if (this.actualizarConyuge) {
          memberData.estadoCivil = 'Casado(a)';
        }
        await this.actualizarMiembroCallable({ churchId, id: this.memberId, datos: memberData });

        if (this.actualizarConyuge) {
          const dataConyuge = {
            conyugeId: this.memberId,
            conyugeNombre: this.memberDetailForm.get('nombres')?.value + ' ' + this.memberDetailForm.get('apellidos')?.value,
            estadoCivil: 'Casado(a)',
            familiaId: memberData.familiaId,
            tieneConyuge: 'SI',
            //Falta seguir actualizando los hijos
          }
          await this.actualizarMiembroCallable({ churchId, id: this.memberDetailForm.get('conyugeId')?.value, datos: dataConyuge })
        }

        if (this.actualizarHijos) {
          let padreId: string | null;
          let madreId: string | null;

          if (memberData.genero === 'M') {
            padreId = this.memberId;
            madreId = this.conyugeId || memberData.conyugeId || null;
          } else {
            madreId = this.memberId;
            padreId = this.conyugeId || memberData.conyugeId || null;
          }
          const data = {
            madreId: madreId,
            padreId: padreId,
            familiaId: memberData.familiaId
          }
          this.selectedHijos.forEach(async hijo => {
            await this.actualizarMiembroCallable({ churchId, id: hijo.id, datos: data })
          });
        }

        // 2. GESTIONAR LA LÓGICA FAMILIAR (si es necesario)
        const familyIdForm = this.memberDetailForm.get('familiaId')?.value as string | null;
        const spouseId: string | null = memberData.conyugeId || null;

        // Dedupe de hijos por id
        const childIds: string[] = Array.from(new Set(
          (memberData.hijos || []).map((h: any) => h?.id).filter(Boolean)
        ));

        // Si no hay cambios en cónyuge/hijos y ya hay familia, no hacemos nada
        const noFamilyOps = !spouseId && childIds.length === 0;
        if (noFamilyOps) {
          // Nada que mover/crear
        } else {
          // A) Si NO hay familia aún pero hay cónyuge/hijos → crea familia y mueve
          if (!familyIdForm) {
            if (!this.validateLastNamesForFamilyCreation(!!spouseId)) {
              // Sin apellidos no se puede nombrar la familia correctamente
            } else {
            // nombre sugerido: “Familia {apellido esposo} {apellido esposa}”
            const nombre = this.suggestFamilyNameFromValues?.() ?? 'Familia Nueva';

            // 1) Crear familia
            const createRes: any = await firstValueFrom(
              this.familyActions.createFamily(this.churchId, { nombre })
            );
            const newFamilyId: string = createRes?.familyId ?? createRes;
            if (!newFamilyId) {
              this.toastService.showWarning(this.translate.instant('MEMBER_DETAIL.TOAST_WARN_FAMILY_NO_ID'));
            } else {
              // 2) Mover miembro base
              const meRole = this.roleFromGenderValue(this.memberDetailForm.get('genero')?.value);
              await firstValueFrom(
                this.familyActions.bulkMoveToFamily(this.churchId, newFamilyId, [
                  { memberId: this.memberId!, role: meRole }
                ])
              );
              // Actualiza el form localmente
              this.memberDetailForm.get('familiaId')?.setValue(newFamilyId, { emitEvent: false });

              // (Opcional) armar set de quienes YA están en la nueva familia (ahora al menos el miembro actual)
              const currentMembersNew = this.dataService.getCurrentMembers?.() || [];
              const alreadyInNew = new Set(
                currentMembersNew.filter(m => m.familiaId === newFamilyId).map(m => m.id)
              );
              alreadyInNew.add(this.memberId!); // por si la suscripción aún no refresca

              // 3) Mover cónyuge si aplica y no está ya
              if (spouseId && !alreadyInNew.has(spouseId)) {
                const spouseRole = this.inferSpouseRoleForCurrent();
                await firstValueFrom(
                  this.familyActions.bulkMoveToFamily(this.churchId, newFamilyId, [
                    { memberId: spouseId, role: spouseRole }
                  ])
                );
                alreadyInNew.add(spouseId);
              }

              // 4) Mover hijos si aplica y no están ya
              const childIdsToMove = childIds.filter(id => !alreadyInNew.has(id));
              if (childIdsToMove.length) {
                await firstValueFrom(
                  this.familyActions.bulkMoveToFamily(
                    this.churchId,
                    newFamilyId,
                    childIdsToMove.map(id => ({ memberId: id, role: 'HIJO' }))
                  )
                );
              }

              // Persistir familiaId en documentos de miembros (el guardado anterior fue sin familiaId)
              await this.actualizarMiembroCallable({
                churchId,
                id: this.memberId!,
                datos: { familiaId: newFamilyId },
              });
              if (spouseId) {
                await this.actualizarMiembroCallable({
                  churchId,
                  id: spouseId,
                  datos: { familiaId: newFamilyId },
                });
              }
              for (const cid of childIds) {
                await this.actualizarMiembroCallable({
                  churchId,
                  id: cid,
                  datos: { familiaId: newFamilyId },
                });
              }
            }
            }
          } else {
            // B) Ya hay familia → solo mover cónyuge/hijos seleccionados a esa familia
            // Construimos un Set con los miembros que YA están en esa familia
            const currentMembers = this.dataService.getCurrentMembers?.() || [];
            const alreadyInFamily = new Set(
              currentMembers.filter(m => m.familiaId === familyIdForm).map(m => m.id)
            );

            const ops: Promise<any>[] = [];

            // Cónyuge (si no está ya)
            if (spouseId && !alreadyInFamily.has(spouseId)) {
              const spouseRole = this.inferSpouseRoleForCurrent();
              ops.push(firstValueFrom(
                this.familyActions.bulkMoveToFamily(this.churchId, familyIdForm, [
                  { memberId: spouseId, role: spouseRole }
                ])
              ));
            }

            // Hijos (solo los que no están ya)
            const childIdsToMove = childIds.filter(id => !alreadyInFamily.has(id));
            if (childIdsToMove.length) {
              ops.push(firstValueFrom(
                this.familyActions.bulkMoveToFamily(
                  this.churchId,
                  familyIdForm,
                  childIdsToMove.map(id => ({ memberId: id, role: 'HIJO' }))
                )
              ));
            }

            if (ops.length) await Promise.all(ops);
          }
        }

        // 3. SUBIR FOTO SI APLICA
        if (this.updatePhoto) {
          await this.subirFotoSiPendiente(this.churchId, this.memberId);
          this.updatePhoto = false;
        }

        this.markFormSaved();
        this.toastService.showSuccess(this.translate.instant('MEMBER_DETAIL.TOAST_OK_MEMBER_UPDATED'));
      } catch (error) {
        this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_SAVE'));
      } finally {
        this.spinnerService.hide();
      }
    } else {
      try {
        const email = this.memberDetailForm.get('email')?.value;
        if (!this.verificarEmailMiembro(email)) {
          this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_EMAIL_DUPLICATE'));
          return;
        }

        // 1. Normalizar hijos igual que en edición
        const tieneHijos = this.memberDetailForm.get('tieneHijos')?.value;
        memberData.hijos = tieneHijos
          ? (memberData.hijos || [])
            .filter((hijo: any) => (hijo.nombre && (typeof hijo.nombre === 'string' ? hijo.nombre.trim() : hijo.nombre.id)))
            .map((hijo: any) => {
              const esNombreObjeto = typeof hijo.nombre === 'object' && hijo.nombre !== null;
              const id = esNombreObjeto ? hijo.nombre.id : null;
              const nombre = esNombreObjeto ? hijo.nombre.nombreCompleto : hijo.nombre;
              const genero = hijo.genero || (esNombreObjeto ? hijo.nombre.genero : null);
              let fechaNacimiento = hijo.fechaNacimiento || (esNombreObjeto ? hijo.nombre.fechaNacimiento : null);

              if (fechaNacimiento && typeof fechaNacimiento.toDate === 'function') {
                fechaNacimiento = fechaNacimiento.toDate();
              }

              return { id, nombre, genero, fechaNacimiento: fechaNacimiento || null };
            })
          : [];

        if (!Array.isArray(memberData.roles)) {
          memberData.roles = [];
        }

        // 2. Crear el nuevo miembro
        const resp: any = await this.crearMiembroCallable({ churchId, ...memberData });
        this.memberId = resp.data.id;
        this.creando = false;
        this.markFormSaved();

        this.toastService.showSuccess(this.translate.instant('MEMBER_DETAIL.TOAST_OK_MEMBER_CREATED'));
        this.router.navigate(['/dashboard/members']);

      } catch (error) {
        this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_CREATE'));
      } finally {
        this.spinnerService.hide();
      }
    }
  }

  onCancel(): void {
    this.router.navigate(['/dashboard/members']);
  }

  normalizarFamilia(miembros: any[]) {
    return miembros
      .map(m => ({
        id: m.id,
        rol: m.rol,
        genero: m.genero || null,
        fechaNacimiento: m.fechaNacimiento instanceof Date
          ? m.fechaNacimiento.getTime()
          : (m.fechaNacimiento?.toDate?.()?.getTime?.() ?? null)
      }))
      .sort((a, b) => a.id.localeCompare(b.id));
  }

  async removeConyuge(): Promise<void> {
    const churchId = this.dataService.getChurchId();
    if (!churchId) {
      this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_NO_CHURCH'));
      return;
    }

    const currentMemberId = this.memberId;
    const conyugeIdToRemove = this.memberDetailForm.get('conyugeId')?.value;

    if (!currentMemberId) {
      this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_MEMBER_ID'));
      return;
    }

    if (!conyugeIdToRemove) {
      this.toastService.showWarning(this.translate.instant('MEMBER_DETAIL.TOAST_WARN_NO_SPOUSE'));
      return;
    }

    this.spinnerService.show();
    try {
      this.familyActions.unlinkSpouse(this.churchId, currentMemberId)
        .pipe(take(1))
        .subscribe({
          next: () => { this.toastService.showSuccess(this.translate.instant('MEMBER_DETAIL.TOAST_OK_SPOUSE_UNLINKED')); this.spinnerService.hide(); },
          error: () => { this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_UNLINK')); this.spinnerService.hide(); }
        });
      // 1. Desvincular cónyuge del miembro actual
      await this.actualizarMiembroCallable({
        churchId,
        id: currentMemberId,
        datos: {
          conyugeId: null,
          conyugeNombre: null,
          tieneConyuge: null, // Resetear el estado del radio button
          estadoCivil: 'Soltero(a)' // Opcionalmente actualizar el estado civil
        }
      });

      // 2. Desvincular miembro actual del cónyuge (si el cónyuge también es un miembro en la DB)
      await this.actualizarMiembroCallable({
        churchId,
        id: conyugeIdToRemove,
        datos: {
          conyugeId: null,
          conyugeNombre: null,
          tieneConyuge: null, // Resetear el estado del radio button del cónyuge
          estadoCivil: 'Soltero(a)' // Opcionalmente actualizar el estado civil del cónyuge
        }
      });

      // 3. Actualizar el estado local del formulario inmediatamente
      this.memberDetailForm.get('conyugeId')?.setValue(null);
      this.memberDetailForm.get('conyugeNombre')?.setValue('');
      this.memberDetailForm.get('tieneConyuge')?.setValue(null);
      this.memberDetailForm.get('estadoCivil')?.setValue('Soltero(a)');
      this.mostrarConyuge = false; // Ocultar la sección del cónyuge en la UI

      // Importante: Si la gestión de la familia (colección 'familias') necesita
      // ser actualizada inmediatamente después de desvincular al cónyuge,
      // deberías llamar a 'gestionarFamiliaCallable' aquí, similar a cómo se hace
      // en el 'onSubmit'. Esto dependerá de la lógica de tu backend para las familias.
      // Por simplicidad en esta respuesta, solo se desvinculan los miembros.
      // Si el campo 'familiaId' en el miembro también debe ser nullificado, asegúrate
      // de que 'actualizarMiembroCallable' lo maneje, o añádelo explícitamente aquí
      // si no hay hijos en la familia.

      this.toastService.showSuccess(this.translate.instant('MEMBER_DETAIL.TOAST_OK_SPOUSE_REMOVED'));
    } catch (error) {
      this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_REMOVE_SPOUSE'));
    } finally {
      this.spinnerService.hide();
    }
  }

  get f(): { [key: string]: AbstractControl } {
    return this.memberDetailForm.controls;
  }

  get hijosFormArray(): FormArray {
    return this.memberDetailForm.get('hijos') as FormArray;
  }

  get hijosControles(): AbstractControl[] {
    return this.hijosFormArray.controls;
  }

  get notasPastoralesFormArray(): FormArray {
    return this.memberDetailForm.get('notasPastorales') as FormArray;
  }

  get notasFormArray(): AbstractControl[] {
    return this.notasPastoralesFormArray.controls;
  }

  private cargarDatosConyugeDesdeDataService(conyugeId: string): void {
    const conyuge = this.todosLosMiembros.find(m => m.id === conyugeId);
    if (conyuge && conyuge.genero !== undefined) {
      this.generoConyuge = conyuge.genero;
      this.fechaNacimientoConyuge = conyuge.fechaNacimiento
    }
  }

  buscarMiembros(event: any) {
    const query = event.query.toLowerCase();

    this.opcionesConyuge = this.todosLosMiembros
      .filter(member =>
        (`${member.nombres} ${member.apellidos || ''}`.toLowerCase().includes(query))
      )
      .map(member => ({
        ...member,
        nombreCompleto: `${member.nombres} ${member.apellidos || ''}`,
        id: member.id
      }));
  }

  seleccionarConyuge(event: any) {
    const seleccionado = event.value;
    this.selectedConyuge = seleccionado || null;
    this.conyugeGenero = seleccionado.genero || '';
    this.conyugeApellido = (seleccionado.apellidos || '').split(' ')[0] || '';
    this.fechaNacimientoConyuge = seleccionado.fechaNacimiento;
    this.memberDetailForm.get('conyugeId')?.setValue(seleccionado.id);
    this.memberDetailForm.get('conyugeNombre')?.setValue(seleccionado.nombres + ' ' + seleccionado.apellidos);
    this.actualizarConyuge = true;

    // Validar que el cónyuge tenga género configurado
    if (!this.conyugeGenero || !['M', 'F'].includes(this.conyugeGenero)) {
      this.toastService.showWarning(this.translate.instant('MEMBER_DETAIL.TOAST_WARN_SPOUSE_GENDER'));
    }
  }

  seleccionarHijo(event: any, index: number) {
    const hijoSeleccionado = event.value;
    this.actualizarHijos = true;
    const hijosArray = this.memberDetailForm.get('hijos') as FormArray;
    const hijoControl = hijosArray.at(index);
    this.selectedHijos = this.selectedHijos || [];
    this.selectedHijos[index] = hijoSeleccionado;

    // Convertir fechaNacimiento de Timestamp a Date si es necesario
    const fechaNacimiento = hijoSeleccionado.fechaNacimiento && typeof (hijoSeleccionado.fechaNacimiento as any).toDate === 'function'
      ? (hijoSeleccionado.fechaNacimiento as any).toDate()
      : hijoSeleccionado.fechaNacimiento;
    hijoControl.patchValue({
      nombre: {
        id: hijoSeleccionado.id,
        nombreCompleto: hijoSeleccionado.nombres + ' ' + hijoSeleccionado.apellidos,
        genero: hijoSeleccionado.genero || null,
        fechaNacimiento: fechaNacimiento || null
      },
      id: hijoSeleccionado.id,
      genero: hijoSeleccionado.genero || null,
      fechaNacimiento: fechaNacimiento || null
    });
  }

  get hijosFA(): FormArray {
    return this.memberDetailForm.get('hijos') as FormArray;
  }

  private newHijoGroup(): FormGroup {
    return this.fb.group({
      nombre: [null],        // string u objeto {id, nombreCompleto, ...}
      id: [null],
      genero: [null],
      fechaNacimiento: [null]
    });
  }

  addChildRow(): void {
    this.hijosFA.push(this.newHijoGroup());
    // Mantén alineado el arreglo de sugerencias por índice
    this.opcionesHijo ||= [];
    this.opcionesHijo.push([]);
    this.cantidadHijos = this.hijosFA.length;
  }

  async onFotoSeleccionada(event: any) {
    const file: File = event.target.files?.[0];
    if (!file) return;

    const formatosPermitidos = ['image/jpeg', 'image/png', 'image/webp'];
    if (!formatosPermitidos.includes(file.type)) {
      this.toastService.showWarning(this.translate.instant('MEMBER_DETAIL.TOAST_WARN_IMAGE_FORMAT'));
      return;
    }

    this.spinnerService.show();
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const originalBase64 = reader.result as string;

          // Comprimir y redimensionar
          const compressed = await this.imageCompress.compressFile(originalBase64, -1, 75, 75);
          const resizedBase64 = await this.imageService.resizeImageBase64(compressed, 250, 250);

          // 👉 Solo previsualizar y marcar como “pendiente de subir”
          this.previewFotoBase64 = resizedBase64;
          this.fotoPendienteBase64 = resizedBase64;

          // Refresca el avatar
          this.memberDetailForm.get('photoUrl')?.setValue(resizedBase64);
          this.updatePhoto = true;
          this.toastService.showInfo(this.translate.instant('MEMBER_DETAIL.TOAST_INFO_PHOTO_READY'));
        } catch (e) {
          this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_IMAGE'));
        } finally {
          this.spinnerService.hide();
        }
      };
      reader.readAsDataURL(file);
    } catch {
      this.spinnerService.hide();
    }
  }

  private async subirFotoSiPendiente(churchId: string, memberId: string) {
    if (!this.fotoPendienteBase64) return null;

    const storage = getStorage();
    const rutaRef = ref(storage, `iglesias/${churchId}/perfiles/${memberId}.jpg`);

    await uploadString(rutaRef, this.fotoPendienteBase64, 'data_url');
    const url = await getDownloadURL(rutaRef);

    // Actualiza miembro
    const miembroRef = doc(this.firestore, `iglesias/${churchId}/miembros/${memberId}`);
    await setDoc(miembroRef, { photoUrl: url }, { merge: true });

    // Si existe usuarioUid válido, actualiza usuarios y (opcional) claims
    const esUidValido = typeof this.usuarioUid === 'string' && this.usuarioUid.trim().length === 28;
    if (esUidValido) {
      const usuarioRef = doc(this.firestore, `iglesias/${churchId}/usuarios/${this.usuarioUid}`);
      await setDoc(usuarioRef, { photoUrl: url }, { merge: true });

      try {
        const functions = getFunctions();
        const updateUserClaims = httpsCallable(functions, 'updateUserProfile');
        await updateUserClaims({ uid: this.usuarioUid, photoUrl: url });
      } catch {
        // no bloquees el flujo si falla la función
      }
    }

    // Limpia el pendiente y deja el preview apuntando al URL final
    this.fotoPendienteBase64 = null;
    this.previewFotoBase64 = url;
    this.memberDetailForm.get('photoUrl')?.setValue(url);
    return url;
  }

  onTieneHijosChange(tieneHijosValue: boolean) {
    if (!tieneHijosValue) {
      // Si no tiene hijos, limpiar el FormArray y las opciones
      this.hijosFormArray.clear();
      this.opcionesHijo = []; // Limpiar las opciones de autocompletado
    } else {
      // Si ahora tiene hijos, asegurar que haya al menos un input si no hay ninguno
      if (this.hijosFormArray.length === 0) {
        this.agregarHijo();
      }
    }
  }

  agregarHijo() {
    this.hijosFormArray.push(this.fb.group({
      nombre: [''],
      id: [null],
      genero: [null], // Añadir genero con valor inicial null
      fechaNacimiento: [null] // Añadir fechaNacimiento con valor inicial null
    }));
    this.opcionesHijo.push([]); // Inicializar array de opciones para el nuevo hijo
    this.cantidadHijos = this.hijosFormArray.length; // Actualizar la cantidad de hijos
  }

  private normalizarHijo(raw: any) {
    const esNombreObjeto = typeof raw?.nombre === 'object' && raw?.nombre !== null;
    const id = raw?.id ?? (esNombreObjeto ? raw.nombre.id : null);
    const nombre = esNombreObjeto ? raw.nombre.nombreCompleto : raw?.nombre;
    const genero = raw?.genero ?? (esNombreObjeto ? raw.nombre.genero : null);
    let fechaNacimiento = raw?.fechaNacimiento ?? (esNombreObjeto ? raw.nombre.fechaNacimiento : null);
    if (fechaNacimiento && typeof fechaNacimiento?.toDate === 'function') {
      fechaNacimiento = fechaNacimiento.toDate();
    }
    return id ? { id, nombre, genero, fechaNacimiento: fechaNacimiento || null } : null;
  }

  eliminarHijo(index: number) {
    const hijosArray = this.memberDetailForm.get('hijos') as FormArray;
    const hijoFG = hijosArray.at(index) as FormGroup;

    // 1) Obtener el id del hijo de forma segura
    const hijoId: string | null =
      hijoFG.get('id')?.value ??
      hijoFG.get('nombre')?.value?.id ??
      this.selectedHijos?.[index]?.id ??
      null;

    if (!hijoId) {
      this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_CHILD_ID'));
      return;
    }

    const nombreHijo =
      hijoFG.get('nombre')?.value?.nombreCompleto ??
      hijoFG.get('nombre')?.value ??
      this.translate.instant('MEMBER_DETAIL.LABEL_THIS_MEMBER');

    this.confirmationService.confirm({
      message: this.translate.instant('MEMBER_DETAIL.CONFIRM_REMOVE_CHILD_MESSAGE', { name: nombreHijo }),
      header: this.translate.instant('MEMBER_DETAIL.CONFIRM_REMOVE_CHILD_HEADER'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('MEMBER_DETAIL.CONFIRM_REMOVE_CHILD_ACCEPT'),
      rejectLabel: this.translate.instant('COMMON.CANCEL'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: async () => {
        this.spinnerService.show();
        try {
          const churchId = this.churchId;

          // 2) Quitar de la colección familias
          await firstValueFrom(this.familyActions.removeMember(churchId, hijoId, false));

          // 3) Limpiar el documento del HIJO
          await this.actualizarMiembroCallable({
            churchId,
            id: hijoId,
            datos: { familiaId: null, padreId: null, madreId: null }
          });

          // 4) Construir el array de hijos del PADRE/MADRE **filtrado**
          const hijosRaw = (this.memberDetailForm.get('hijos') as FormArray).getRawValue() as any[];

          // Normaliza y filtra
          const hijosNormalizados = hijosRaw
            .map(h => this.normalizarHijo(h))
            .filter((h): h is { id: string, nombre: string, genero: any, fechaNacimiento: any } => !!h);

          const hijosSinElRemovido = hijosNormalizados.filter(h => h.id !== hijoId);
          const tieneHijosNuevo = hijosSinElRemovido.length > 0;

          // 5) Actualizar SOLO los campos necesarios en el documento del PADRE/MADRE
          await this.actualizarMiembroCallable({
            churchId,
            id: this.memberId!,
            datos: {
              hijos: hijosSinElRemovido,
              tieneHijos: tieneHijosNuevo
            }
          });

          // 6) UI: quitar el hijo del FormArray tras éxito en backend
          hijosArray.removeAt(index);
          this.opcionesHijo.splice(index, 1);
          this.cantidadHijos = hijosArray.length;

          if (!tieneHijosNuevo) {
            this.memberDetailForm.get('tieneHijos')?.setValue(false);
            this.tieneHijos = false;
          }

          this.toastService.showSuccess(this.translate.instant('MEMBER_DETAIL.TOAST_OK_CHILD_REMOVED'));
        } catch (error) {
          this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_REMOVE_CHILD'));
        } finally {
          this.spinnerService.hide();
        }
      }
    });
    if (this.memberDetailForm.get('tieneHijos')?.value && this.hijosFA.length === 0) {
      this.addChildRow();
    }
  }

  buscarMiembrosHijos(event: any, index: number) {
    const query = event.query.toLowerCase();

    const currentMemberId = this.memberId;
    const selectedSpouseId = this.memberDetailForm.get('conyugeId')?.value;

    this.opcionesHijo[index] = this.todosLosMiembros
      .filter(member =>
        member.nombres.toLowerCase().includes(query) &&
        member.id !== currentMemberId && // Excluir al miembro actual
        member.id !== selectedSpouseId   // Excluir al cónyuge
      )
      .map(member => ({
        ...member,
        nombreCompleto: `${member.nombres} ${member.apellidos || ''}`,
        id: member.id
      }));
  }

  private cargarDatosFamiliares(): void {
    if (!this.originalMemberData || !this.todosLosMiembros) return;

    const hijosEnDocumento = this.originalMemberData.hijos || [];
    const conyugeId = this.originalMemberData.conyugeId;

    // 🔹 Actualizar hijos con data completa si existe en todosLosMiembros
    const hijosEnriquecidos = hijosEnDocumento.map((hijo: any) => {
      const hijoCompleto = this.todosLosMiembros.find(m => m.id === hijo.id);

      return {
        id: hijo.id,
        nombre: hijo.nombre || hijoCompleto?.nombres + ' ' + (hijoCompleto?.apellidos || ''),
        genero: hijo.genero || hijoCompleto?.genero || null,
        fechaNacimiento: hijo.fechaNacimiento || hijoCompleto?.fechaNacimiento || null
      };
    });

    this.originalMemberData.hijos = hijosEnriquecidos;

    // 🔹 Si hay cónyuge, actualizar su info también
    if (conyugeId) {
      const conyuge = this.todosLosMiembros.find(m => m.id === conyugeId);
      if (conyuge) {
        this.conyugeGenero = conyuge.genero || null;
        this.fechaNacimientoConyuge = conyuge.fechaNacimiento || null;

        // También actualizar en la data original si no estaban
        if (!this.originalMemberData.conyugeNombre) {
          this.originalMemberData.conyugeNombre = `${conyuge.nombres} ${conyuge.apellidos || ''}`;
        }
      }
    }
  }

  private fechaIgual(f1: any, f2: any): boolean {
    if (!f1 && !f2) return true;
    if (!f1 || !f2) {
      return false;
    }
    const time1 = new Date(f1).getTime();
    const time2 = new Date(f2).getTime();
    return time1 === time2;
  }

  getRolesYMinisterios(member: Member): string {
    const resultados: string[] = [];

    // Roles simples
    if (member.roles && member.roles.length > 0) {
      if (typeof member.roles[0] === 'string') {
        resultados.push(
          ...member.roles
            .filter((r: string) => r.toLowerCase() !== 'liderazgo')  // omite "liderazgo"
            .map((r: string) => this.capitalize(r))
        );
      } else {
        resultados.push(
          ...member.roles
            .filter((r: any) => r.value?.toLowerCase() !== 'liderazgo')  // omite si value es "liderazgo"
            .map((r: any) => this.capitalize(r.label))
        );
      }
    }
    // Ministerios: id/rol
    if (member.ministerios && member.ministerios.length > 0) {
      const ministerios = member.ministerios.map((m: any) => {
        const id = this.capitalize(m.id || '');
        const rol = this.capitalize(m.rol || '');
        return `${id} / ${rol}`;
      });
      resultados.push(...ministerios);
    }

    return resultados.join(', ');
  }

  capitalize(text: string): string {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  calcularTiempo(fechaNacimiento?: Date | Timestamp): string {
    if (!fechaNacimiento) return '';

    let nacimiento: Date;

    if (fechaNacimiento instanceof Timestamp) {
      nacimiento = fechaNacimiento.toDate();
    } else if (fechaNacimiento instanceof Date) {
      nacimiento = fechaNacimiento;
    } else {
      return '';
    }

    const hoy = new Date();

    let edadAnios = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    const dia = hoy.getDate() - nacimiento.getDate();

    // Ajuste si aún no ha cumplido años este año
    if (mes < 0 || (mes === 0 && dia < 0)) {
      edadAnios--;
    }

    if (edadAnios >= 1) {
      return `${edadAnios} año${edadAnios > 1 ? 's' : ''}`;
    }

    // Si es menor de 1 año, calcular meses
    let edadMeses = hoy.getMonth() - nacimiento.getMonth();
    if (edadMeses < 0) edadMeses += 12;
    if (hoy.getDate() < nacimiento.getDate()) edadMeses--; // aún no cumple el mes completo

    edadMeses = edadMeses < 0 ? 0 : edadMeses;

    return `${edadMeses} mes${edadMeses !== 1 ? 'es' : ''}`;
  }

  abrirModalEstudio() {
    this.nuevoEstudioTexto = '';
    this.mostrarModalEstudio = true;
  }

  agregarEstudioBiblico() {
    if (!this.nuevoEstudioTexto.trim()) return;
    const campo = this.memberDetailForm.get('estudiosBiblicos');
    const valorActual = campo?.value || '';
    // Inserta arriba con salto de línea
    const nuevoTexto = this.nuevoEstudioTexto.trim();
    const nuevoCampo = valorActual ? `${valorActual} - ${nuevoTexto}` : nuevoTexto;
    campo?.setValue(nuevoCampo);
    this.mostrarModalEstudio = false;
  }

  abrirModalSalud() {
    this.nuevoEstadoSalud = '';
    this.mostrarModalSalud = true;
  }

  agregarEstadoSalud() {
    if (!this.nuevoEstadoSalud.trim()) return;
    const campo = this.memberDetailForm.get('estadosDeSalud');
    const valorActual = campo?.value || '';
    // Inserta arriba con salto de línea
    const nuevoTexto = this.nuevoEstadoSalud.trim();
    const nuevoCampo = valorActual ? `${valorActual} - ${nuevoTexto}` : nuevoTexto;
    campo?.setValue(nuevoCampo);
    this.mostrarModalSalud = false;
  }

  abrirModalNota() {
    this.nuevoContenidoNota = '';
    this.mostrarModalNota = true;
  }

  agregarNota() {
    if (!this.nuevoContenidoNota.trim()) return;

    const autor = resolveLoggedInDisplayName({
      authDisplayName: this.usuarioNombre,
      email: this.usuarioEmail,
    }) || this.translate.instant('MEMBER_DETAIL.NOTE_AUTHOR_FALLBACK');

    const nuevaNota = this.fb.group({
      fecha: new Date(),
      autor,
      contenido: this.nuevoContenidoNota.trim()
    });
    this.notasPastoralesFormArray.insert(0, nuevaNota);
    this.mostrarModalNota = false;
  }

  eliminarNota(index: number) {
    const notasArray = this.memberDetailForm.get('notasPastorales') as FormArray;
    if (notasArray && notasArray.length > index) {
      notasArray.removeAt(index);
    }
  }

  async verificarEmailMiembro(
    email: string,
    opts?: { excludeMemberId?: string; excludeOriginalEmail?: string }
  ): Promise<boolean> {
    const norm = (email ?? '').trim().toLowerCase();
    if (!norm) return true; // vacío no bloquea

    const { excludeMemberId, excludeOriginalEmail } = opts ?? {};

    const miembros = this.todosLosMiembros ?? [];

    return !miembros.some((m: any) => {
      const mid = m.id ?? m.memberId ?? null;

      // 1) Si estoy editando y tengo id, ignoro mi propio doc
      if (excludeMemberId && mid && mid === excludeMemberId) return false;

      // 2) Si estoy editando pero NO tengo id en cache, ignoro si el email es el mismo que el original
      const mEmailNorm = ((m.emailNorm ?? m.email ?? '') + '').trim().toLowerCase();
      if (excludeOriginalEmail && mEmailNorm === excludeOriginalEmail.trim().toLowerCase()) {
        return false;
      }

      // 3) Comparación normal
      return mEmailNorm === norm;
    });
  }

  isInvalidRequired(ctrlName: string): boolean {
    const c = this.memberDetailForm.get(ctrlName);
    if (!c) return false;

    // Angular 14+: hasValidator existe
    const hasReq = (c as any).hasValidator
      ? (c as any).hasValidator(Validators.required)
      : this._hasRequiredFallback(c);

    return hasReq && c.invalid && c.touched;
  }

  // Solo por si tu Angular es viejo y no tiene hasValidator:
  private _hasRequiredFallback(control: AbstractControl): boolean {
    const v = control.validator ? control.validator({} as any) : null;
    return !!v && !!v['required'];
  }

  private aplicarMiembroAlFormulario(member: any): void {
    const memberDataForForm = { ...member };
    // Área de servicio (texto)
    const rolesYMinisterios = this.getRolesYMinisterios(member);
    this.memberDetailForm.get('areaServicio')?.setValue(memberDataForForm.areaServicio);
    //FALTA IMPLEMENTAR PARA QUE ES rolesYMinisterios

    // Datos base
    this.originalMemberData = { ...member };
    this.usuarioUid = memberDataForForm.uid;
    this.memberApellido = (memberDataForForm.apellidos || '').split(' ')[0] || '';
    this.edadMiembro = this.calcularTiempo(memberDataForForm.fechaNacimiento);
    this.antiguedadIglesia = this.calcularTiempo(memberDataForForm.fechaIngreso);

    // Timestamps → Date
    const toDateIfTs = (v: any) => (v && typeof v?.toDate === 'function') ? v.toDate() : v;
    memberDataForForm.fechaNacimiento = toDateIfTs(memberDataForForm.fechaNacimiento);
    memberDataForForm.fechaBautismo = toDateIfTs(memberDataForForm.fechaBautismo);
    memberDataForForm.fechaIngreso = toDateIfTs(memberDataForForm.fechaIngreso);

    // Cargar conyuge/familia si aplica
    if (memberDataForForm.conyugeId !== null) {
      this.leerFamilia(memberDataForForm.familiaId);
      this.cargarDatosConyugeDesdeDataService(memberDataForForm.conyugeId);
      this.cargarDatosFamiliares();
      this.dataFamiliaOriginal = this.construirDataFamilia(memberDataForForm);
      this.memberDetailForm.get('tieneConyuge')?.setValue('SI');
      this.tieneConyugeBtn = true;
    }

    // Deshabilitar campos
    this.memberDetailForm.get('fechaCreacion')?.disable();
    this.memberDetailForm.get('fechaActualizacion')?.disable();
    this.memberDetailForm.get('departamentoEdad')?.disable();
    this.memberDetailForm.get('ministerios')?.disable();
    this.memberDetailForm.get('roles')?.disable();

    // Estado civil (normalización)
    const conversionEstadoCivil: Record<string, string> = {
      'Soltero(a)': 'Soltero(a)',
      'Casado(a)': 'Casado(a)',
      'Viudo(a)': 'Viudo(a)',
      'Divorciado(a)': 'Divorciado(a)'
    };
    const estadoFirestore = memberDataForForm.estadoCivil;
    memberDataForForm.estadoCivil = conversionEstadoCivil[estadoFirestore!] ?? '';

    // Textos roles/ministerios (si los sigues usando)
    this.rolesTexto = (memberDataForForm.roles || [])
      .filter((role: any) => typeof role === 'string')
      .map((role: string) => role.charAt(0).toUpperCase() + role.slice(1).toLowerCase())
      .join(', ');
    this.ministeriosTexto = (memberDataForForm.ministerios || [])
      .map((m: any) => `${m.rol} de ${m.id}`).join(', ');

    // Parchar el formulario principal
    this.memberDetailForm.patchValue(memberDataForForm);

    // Conyuge toggle
    if (memberDataForForm.conyugeId) {
      this.memberDetailForm.get('tieneConyuge')?.setValue('SI');
    } else if (memberDataForForm.conyugeNombre) {
      this.memberDetailForm.get('tieneConyuge')?.setValue('NO');
    } else {
      this.memberDetailForm.get('tieneConyuge')?.setValue(null);
    }

    // Notas / Hijos
    this.setNotasPastorales(memberDataForForm.notasPastorales || []);
    this.setHijos(memberDataForForm.hijos || []);

    // Reacciones a cambios
    this.memberDetailForm.get('estadoCivil')?.valueChanges.subscribe(value => {
      this.mostrarConyuge = value === 'Casado(a)';
      if (!this.mostrarConyuge) {
        this.memberDetailForm.get('tieneConyuge')?.setValue(null);
        this.memberDetailForm.get('conyugeId')?.setValue(null);
        this.memberDetailForm.get('conyugeNombre')?.setValue('');
      }
    });
    this.memberDetailForm.get('tieneHijos')?.valueChanges.subscribe(value => {
      this.onTieneHijosChange(value);
    });
    this.mostrarConyuge = this.memberDetailForm.get('estadoCivil')?.value === 'Casado(a)';
    this.markFormSaved();
    this.spinnerService.hide();
  }

  private markFormSaved(): void {
    this.memberDetailForm.markAsPristine();
    this.actualizarConyuge = false;
    this.actualizarHijos = false;
    this.updatePhoto = false;
  }

  // === ACCIONES: ===
  onLinkSpouseToFamily() {
    const familyId = this.memberDetailForm.get('familiaId')?.value as string | null;

    if (!familyId) {
      this.toastService.showWarning(this.translate.instant('MEMBER_DETAIL.TOAST_WARN_NO_FAMILY'));
      return;
    }

    if (!this.validateLastNamesForFamilyCreation(true)) {
      return;
    }

    if (!this.selectedConyuge?.id) {
      this.toastService.showWarning(this.translate.instant('MEMBER_DETAIL.TOAST_WARN_SELECT_SPOUSE'));
      return;
    }

    // Rol del miembro actual (PADRE/MADRE) según su género:
    const meRole = this.roleFromGenderValue(this.memberDetailForm.get('genero')?.value);
    // Rol complementario para el cónyuge:
    const spouseRole = this.inferSpouseRoleForCurrent();

    this.spinnerService.show();
    this.familyActions
      .linkSpouse(this.churchId, this.memberId!, this.selectedConyuge.id, {
        role: meRole,
        spouseRole,
        nombre: this.suggestFamilyNameFromValues(),
      })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.tieneConyugeBtn = true;
          this.onSubmit(); // Guarda cambios del formulario
        },
        error: () => {
          this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_LINK_SPOUSE'));
          this.spinnerService.hide();
        },
      });
  }

  onCreateFamilyWithSpouse() {
    if (!this.selectedConyuge?.id) {
      this.toastService.showWarning(this.translate.instant('MEMBER_DETAIL.TOAST_WARN_SELECT_SPOUSE'));
      return;
    }

    // FASE 2: Validar género del miembro actual
    const miGenero = this.memberDetailForm.get('genero')?.value;
    if (!miGenero || !['M', 'F'].includes(miGenero)) {
      this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_GENDER_CURRENT'));
      return;
    }

    // FASE 2: Validar género del cónyuge
    if (!this.conyugeGenero || !['M', 'F'].includes(this.conyugeGenero)) {
      this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_GENDER_SPOUSE'));
      return;
    }

    // FASE 2: Calcular roles y validar
    const meRole: FamilyRole | undefined = this.roleFromGenderValue(miGenero);
    const spouseRole: FamilyRole | undefined = this.inferSpouseRoleForCurrent();

    if (!meRole || !spouseRole) {
      this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_FAMILY_ROLES'));
      return;
    }

    if (!this.validateLastNamesForFamilyCreation(true)) {
      return;
    }

    const nombre = this.suggestFamilyNameFromValues();

    this.spinnerService.show();
    this.familyActions.createFamily(this.churchId, { nombre })
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          const familyId = (res as any)?.familyId ?? (res as any);
          if (!familyId) {
            this.toastService.showWarning(this.translate.instant('MEMBER_DETAIL.TOAST_WARN_FAMILY_NO_ID'));
            this.spinnerService.hide();
            return;
          }

          this.familyActions.bulkMoveToFamily(
            this.churchId,
            familyId,
            [
              { memberId: this.memberId!, role: meRole },
              { memberId: this.selectedConyuge?.id!, role: spouseRole },
            ]
          )
            .pipe(take(1))
            .subscribe({
              next: async () => {
                this.memberDetailForm.get('familiaId')?.setValue(familyId);
                try {
                  await this.actualizarMiembroCallable({
                    churchId: this.churchId,
                    id: this.memberId!,
                    datos: { familiaId: familyId },
                  });
                  await this.actualizarMiembroCallable({
                    churchId: this.churchId,
                    id: this.selectedConyuge!.id!,
                    datos: { familiaId: familyId },
                  });
                } catch {
                  this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_SAVE'));
                  this.spinnerService.hide();
                  return;
                }
                this.onLinkSpouseToFamily();
              },
              error: (err) => {
                // FASE 1: Capturar error específico
                const errorMsg = err?.message || err?.error?.message || JSON.stringify(err);
                this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_FAMILY_PARTIAL', { detail: errorMsg }));
                this.spinnerService.hide();
              }
            });
        },
        error: (err) => {
          this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_CREATE_FAMILY'));
          this.spinnerService.hide();
        }
      });
  }

  // Normaliza un hijo que viene de selectedHijos (miembro completo)
  private normalizarDesdeMiembro(m: any) {
    if (!m?.id) return null;
    let fechaNacimiento = m?.fechaNacimiento;
    if (fechaNacimiento && typeof fechaNacimiento?.toDate === 'function') fechaNacimiento = fechaNacimiento.toDate();
    return {
      id: m.id,
      nombre: [m.nombres, m.apellidos].filter(Boolean).join(' ').trim(),
      genero: m.genero ?? null,
      fechaNacimiento: fechaNacimiento || null
    };
  }

  async onAddChildrenToFamily(): Promise<void> {
    this.spinnerService.show();

    try {
      const churchId = this.churchId;
      const form = this.memberDetailForm;
      let familyId = form.get('familiaId')?.value as string | null;

      const hijosIds = (this.selectedHijos || [])
        .map(h => h?.id)
        .filter(Boolean) as string[];

      if (!hijosIds.length) {
        this.toastService.showInfo(this.translate.instant('MEMBER_DETAIL.TOAST_INFO_SELECT_CHILD'));
        return;
      }

      // Si no hay familia, créala y mueve al miembro actual
      if (!familyId) {
        const spouseIdForFamily = this.conyugeId || form.get('conyugeId')?.value;
        if (!this.validateLastNamesForFamilyCreation(!!spouseIdForFamily)) {
          return;
        }
        const nombre = this.suggestFamilyNameFromValues?.() ?? 'Familia Nueva';
        const meRole = this.roleFromGenderValue(form.get('genero')?.value);
        const res = await firstValueFrom(this.familyActions.createFamily(churchId, { nombre }));
        familyId = (res as any)?.familyId ?? (res as any);
        if (!familyId) throw new Error(this.translate.instant('MEMBER_DETAIL.ERR_NO_FAMILY_ID'));

        await firstValueFrom(
          this.familyActions.bulkMoveToFamily(churchId, familyId, [{ memberId: this.memberId!, role: meRole }])
        );
        // Actualiza el form sin disparar cascadas
        form.get('familiaId')?.setValue(familyId, { emitEvent: false });
        await this.actualizarMiembroCallable({
          churchId,
          id: this.memberId!,
          datos: { familiaId: familyId },
        });
      }

      // 1) Mover hijos a la familia (colección familias)
      await firstValueFrom(
        this.familyActions.bulkMoveToFamily(
          churchId,
          familyId,
          hijosIds.map(id => ({ memberId: id, role: 'HIJO' as any }))
        )
      );

      // 2) Calcular padre/madre para setear en los documentos de cada hijo
      let padreId: string | null;
      let madreId: string | null;
      if (form.get('genero')?.value === 'M') {
        padreId = this.memberId!;
        madreId = this.conyugeId || form.get('conyugeId')?.value || null;
      } else {
        madreId = this.memberId!;
        padreId = this.conyugeId || form.get('conyugeId')?.value || null;
      }

      // 3) Actualizar documentos de CADA HIJO (familiaId + padreId/madreId)
      await Promise.all(
        hijosIds.map(id =>
          this.actualizarMiembroCallable({
            churchId,
            id,
            datos: { familiaId: familyId, padreId, madreId }
          })
        )
      );

      // 4) Construir el array de hijos del PADRE/MADRE (merge existentes + nuevos, sin duplicados)
      const hijosRaw = (form.get('hijos') as FormArray).getRawValue() as any[];

      const existentes = hijosRaw
        .map(h => this.normalizarHijo(h))
        .filter((h: any) => !!h);

      const nuevos = (this.selectedHijos || [])
        .map(h => this.normalizarDesdeMiembro(h))
        .filter((h: any) => !!h);

      // Merge por id
      const byId = new Map<string, any>();
      for (const h of existentes) byId.set(h?.id, h);
      for (const h of nuevos) byId.set(h?.id, h);

      const hijosDelPadre = Array.from(byId.values());
      const tieneHijosNuevo = hijosDelPadre.length > 0;

      // 5) Actualizar SOLO campos necesarios en el documento del PADRE/MADRE
      await this.actualizarMiembroCallable({
        churchId,
        id: this.memberId!,
        datos: {
          familiaId: familyId,
          hijos: hijosDelPadre,
          tieneHijos: tieneHijosNuevo
        }
      });

      // 6) Si hay cónyuge, actualizar también su documento (familiaId + hijos + tieneHijos)
      if (this.conyugeId || form.get('conyugeId')?.value) {
        const spouseId = this.conyugeId || form.get('conyugeId')?.value;
        await this.actualizarMiembroCallable({
          churchId,
          id: spouseId,
          datos: { familiaId: familyId, hijos: hijosDelPadre, tieneHijos: tieneHijosNuevo }
        });
      }

      this.actualizarHijos = false;
      this.toastService.showSuccess(this.translate.instant('MEMBER_DETAIL.TOAST_OK_CHILDREN_ADDED'));
    } catch (error) {
      this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_ADD_CHILDREN'));
    } finally {
      this.spinnerService.hide();
    }
  }

  onRemoveFromFamily() {
    if (!this.memberId) return;
    this.spinnerService.show();
    this.familyActions.removeMember(this.churchId, this.memberId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.memberDetailForm.get('familiaId')?.setValue(null);
          this.toastService.showSuccess(this.translate.instant('MEMBER_DETAIL.TOAST_OK_REMOVED_FROM_FAMILY'));
          this.spinnerService.hide();
        },
        error: () => {
          this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_REMOVE_MEMBER'));
          this.spinnerService.hide();
        }
      });
  }

  onUnlinkSpouse() {
    if (!this.memberId) return;
    this.spinnerService.show();
    this.familyActions.unlinkSpouse(this.churchId, this.memberId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toastService.showSuccess(this.translate.instant('MEMBER_DETAIL.TOAST_OK_SPOUSE_UNLINKED'));
          this.spinnerService.hide();
        },
        error: () => {
          this.toastService.showError(this.translate.instant('MEMBER_DETAIL.TOAST_ERR_UNLINK_SPOUSE'));
          this.spinnerService.hide();
        }
      });
  }

  private isFemaleFromValue(v: any): boolean | undefined {
    if (v == null) return undefined;
    const s = String(v).toLowerCase();
    if (s.startsWith('f')) return true;   // F, Femenino
    if (s.startsWith('m')) return false;  // M, Masculino
    return undefined;
  }

  private roleFromGenderValue(v: any): FamilyRole | undefined {
    const f = this.isFemaleFromValue(v);
    if (f === true) return 'MADRE';
    if (f === false) return 'PADRE';
    return undefined;
  }

  /** El cónyuge será el rol complementario del miembro actual */
  private inferSpouseRoleForCurrent(): FamilyRole | undefined {
    const meFemale = this.isFemaleFromValue(this.memberDetailForm.get('genero')?.value);
    if (meFemale === true) return 'PADRE';   // si yo soy mujer → cónyuge = PADRE
    if (meFemale === false) return 'MADRE';  // si yo soy hombre → cónyuge = MADRE
    return undefined;
  }

  private memberHasApellidos(): boolean {
    return !!String(this.memberDetailForm.get('apellidos')?.value ?? '').trim();
  }

  /** Apellidos del cónyuge vinculado (documento en socios), según selección o lista cargada */
  private getSpouseApellidosTrimmed(): string {
    const sid = this.memberDetailForm.get('conyugeId')?.value as string | null | undefined;
    if (!sid) return '';
    if (this.selectedConyuge?.id === sid) {
      return String(this.selectedConyuge.apellidos ?? '').trim();
    }
    const m = this.todosLosMiembros.find(x => x.id === sid);
    return String(m?.apellidos ?? '').trim();
  }

  /**
   * Impide crear familia sin apellidos suficientes para formar el nombre.
   * Si hay cónyuge vinculado, exige apellidos en ambos documentos (padre y madre).
   */
  private validateLastNamesForFamilyCreation(requireSpouseLastName: boolean): boolean {
    const hasSelf = this.memberHasApellidos();
    if (!requireSpouseLastName) {
      if (!hasSelf) {
        this.toastService.showWarning(this.translate.instant('MEMBER_DETAIL.TOAST_WARN_FAMILY_LASTNAME_SELF'));
        return false;
      }
      return true;
    }
    const hasSpouse = !!this.getSpouseApellidosTrimmed();
    if (hasSelf && hasSpouse) return true;
    if (!hasSelf && !hasSpouse) {
      this.toastService.showWarning(this.translate.instant('MEMBER_DETAIL.TOAST_WARN_FAMILY_LASTNAME_BOTH'));
    } else if (!hasSelf) {
      this.toastService.showWarning(this.translate.instant('MEMBER_DETAIL.TOAST_WARN_FAMILY_LASTNAME_SELF'));
    } else {
      this.toastService.showWarning(this.translate.instant('MEMBER_DETAIL.TOAST_WARN_FAMILY_LASTNAME_SPOUSE'));
    }
    return false;
  }

  /** Construye “Familia {apellido esposo} {apellido esposa}” */
  private suggestFamilyNameFromValues(): string {
    const memberApellidos = String(this.memberDetailForm.get('apellidos')?.value || '').trim();
    const spouseApellidos = String(this.conyugeApellido || '').trim();
    const memberGenero = this.memberDetailForm.get('genero')?.value;
    const spouseGenero = this.conyugeGenero;

    const firstLastName = (ap: string) => ap ? ap.split(' ')[0] : '';

    let apellidoHombre = '';
    let apellidoMujer = '';

    const memberFirst = firstLastName(memberApellidos);
    const spouseFirst = firstLastName(spouseApellidos);

    const mIsFemale = this.isFemaleFromValue(memberGenero);
    const sIsFemale = this.isFemaleFromValue(spouseGenero);

    if (mIsFemale === true) apellidoMujer = memberFirst;
    if (mIsFemale === false) apellidoHombre = memberFirst;

    if (sIsFemale === true) apellidoMujer = spouseFirst || apellidoMujer;
    if (sIsFemale === false) apellidoHombre = spouseFirst || apellidoHombre;

    if (apellidoHombre && apellidoMujer) return `Familia ${apellidoHombre} ${apellidoMujer}`;
    if (apellidoHombre) return `Familia ${apellidoHombre}`;
    if (apellidoMujer) return `Familia ${apellidoMujer}`;
    return 'Familia Nueva';
  }

  /** FamilyId actualmente mostrado en el formulario */
  get currentFamilyId(): string | null {
    return this.memberDetailForm.get('familiaId')?.value || null;
  }

  /** Set con los IDs de miembros que ya pertenecen a la familia actual (si hay) */
  private getCurrentFamilyMemberIdSet(): Set<string> {
    const fid = this.currentFamilyId;
    if (!fid) return new Set<string>();

    const all = this.dataService.getCurrentMembers?.() || [];

    const ids = all
      .filter(m => m.familiaId === fid)
      .map(m => m.id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0);

    return new Set<string>(ids);
  }

  /** ¿este memberId tiene membresía ACTIVA en la familia que muestra el form? */
  isInDisplayedFamilyActive(memberId?: string | null): boolean {
    if (!memberId) return false;
    const familyId = this.memberDetailForm.get('familiaId')?.value as string | null;
    if (!familyId) return false;

    // usamos el índice por familia (solo activas)
    const byFamily = this.dataService.getMembershipsByFamilySync();
    const arr = byFamily[familyId] || [];
    return arr.some(m => m.memberId === memberId);
  }

  /** ¿Hay familia en el form? */
  hasDisplayedFamily(): boolean {
    return !!this.currentFamilyId;
  }

  /** ¿Todos los hijos seleccionados ya están en la familia? */
  get allSelectedChildrenAlreadyInFamily(): boolean {
    if (!this.hasDisplayedFamily()) return false;
    const set = this.getCurrentFamilyMemberIdSet();
    const arr = this.selectedHijos || [];
    if (arr.length === 0) return false;
    return arr.every(h => !!h?.id && set.has(h.id));
  }

  /** ¿Hay al menos un hijo seleccion. que aún no esté en la familia? */
  get hasChildrenToAdd(): boolean {
    if (!this.hasDisplayedFamily()) return false;
    const set = this.getCurrentFamilyMemberIdSet();
    const arr = this.selectedHijos || [];
    return arr.some(h => !!h?.id && !set.has(h.id));
  }

  /** Obtiene el rol del miembro actual en la familia mostrada */
  get currentMemberRole(): FamilyRole | null {
    const familyId = this.currentFamilyId;
    if (!familyId || !this.memberId) return null;

    const membership = this.dataService.getActiveMembershipForMember(this.memberId);
    return membership?.role || null;
  }

  /** ¿El miembro actual es HIJO en su familia? */
  get isCurrentMemberChild(): boolean {
    return this.currentMemberRole === 'HIJO';
  }

  /** Label dinámico: "Hermanos" si es HIJO, "Hijos" si es PADRE/MADRE */
  get childrenLabel(): string {
    return this.isCurrentMemberChild
      ? this.translate.instant('MEMBER_DETAIL.CHILDREN_LABEL_SIBLINGS')
      : this.translate.instant('MEMBER_DETAIL.CHILDREN_LABEL_CHILDREN');
  }

  /** Obtiene los hijos/hermanos de la familia actual, filtrando al miembro actual si es HIJO */
  get filteredFamilyChildren(): Member[] {
    const familyId = this.currentFamilyId;
    if (!familyId) return [];

    const allMembers = this.dataService.getCurrentMembers();
    const byFamily = this.dataService.getMembershipsByFamilySync();
    const memberships = byFamily[familyId] || [];

    // Filtrar solo los HIJOs
    const childrenIds = memberships
      .filter(m => m.role === 'HIJO')
      .map(m => m.memberId);

    let children = allMembers.filter(m => m.id && childrenIds.includes(m.id));

    // Si el miembro actual es HIJO, excluirlo de la lista
    if (this.isCurrentMemberChild && this.memberId) {
      children = children.filter(c => c.id !== this.memberId);
    }

    return children;
  }

  /** Obtiene el nombre de la familia propia de un miembro (si tiene una como PADRE/MADRE) */
  getMemberOwnFamilyName(memberId: string): string | null {
    return this.dataService.getMemberOwnFamilyName(memberId);
  }

  private buildTranslatedOptions(): void {
    const t = (key: string) => this.translate.instant(key);

    this.estadoCivilOptions = [
      { label: t('MEMBER_DETAIL.OPT_CIVIL_SINGLE'), value: 'Soltero(a)' },
      { label: t('MEMBER_DETAIL.OPT_CIVIL_MARRIED'), value: 'Casado(a)' },
      { label: t('MEMBER_DETAIL.OPT_CIVIL_WIDOWED'), value: 'Viudo(a)' },
      { label: t('MEMBER_DETAIL.OPT_CIVIL_DIVORCED'), value: 'Divorciado(a)' }
    ];

    this.generoOptions = [
      { label: t('MEMBER_DETAIL.OPT_GENDER_MALE'), value: 'M' },
      { label: t('MEMBER_DETAIL.OPT_GENDER_FEMALE'), value: 'F' }
    ];

    this.tipoDocumentoOptions = [
      { label: 'DNI', value: 'DNI' },
      { label: 'NIE', value: 'NIE' },
      { label: t('MEMBER_DETAIL.OPT_DOC_PASSPORT'), value: 'Pasaporte' },
      { label: t('MEMBER_DETAIL.OPT_DOC_NONE'), value: 'Ninguno' }
    ];

    this.nivelesEstudios = [
      { label: t('MEMBER_DETAIL.OPT_EDU_PRIMARY'), value: 'Primaria' },
      { label: t('MEMBER_DETAIL.OPT_EDU_SECONDARY'), value: 'Secundaria' },
      { label: t('MEMBER_DETAIL.OPT_EDU_TECHNICAL'), value: 'T\u00e9cnico' },
      { label: t('MEMBER_DETAIL.OPT_EDU_TECHNOLOGIST'), value: 'Tecn\u00f3logo' },
      { label: t('MEMBER_DETAIL.OPT_EDU_UNIVERSITY'), value: 'Universitario' },
      { label: t('MEMBER_DETAIL.OPT_EDU_POSTGRAD'), value: 'Postgrado' },
      { label: t('MEMBER_DETAIL.OPT_EDU_OTHER'), value: 'Otro' }
    ];

    this.nivelBautismo = [
      { label: t('MEMBER_DETAIL.OPT_BAP_BAPTIZED'), value: 'Bautizado' },
      { label: t('MEMBER_DETAIL.OPT_BAP_NOT_BAPTIZED'), value: 'No Bautizado' },
      { label: t('MEMBER_DETAIL.OPT_BAP_IN_PROGRESS'), value: 'En Proceso' }
    ];

    this.bautizadoEspirituSanto = [
      { label: t('MEMBER_DETAIL.OPT_YES'), value: 'S\u00ed' },
      { label: t('MEMBER_DETAIL.OPT_NO'), value: 'No' }
    ];

    this.estadoParticipacion = [
      { label: t('MEMBER_DETAIL.OPT_STATUS_ACTIVE'), value: 'Activo' },
      { label: t('MEMBER_DETAIL.OPT_STATUS_INACTIVE'), value: 'Inactivo' },
      { label: t('MEMBER_DETAIL.OPT_STATUS_UNRECOGNIZED'), value: 'No Reconocido' },
      { label: t('MEMBER_DETAIL.OPT_STATUS_DISCIPLINE'), value: 'En Disciplina' }
    ];

    this.tipoPersona = [
      { label: t('MEMBER_DETAIL.OPT_TYPE_MEMBER'), value: 'Miembro' },
      { label: t('MEMBER_DETAIL.OPT_TYPE_VISITOR'), value: 'Visitante' },
      { label: t('MEMBER_DETAIL.OPT_TYPE_NEW_BELIEVER'), value: 'Nuevo Creyente' },
      { label: t('MEMBER_DETAIL.OPT_TYPE_RETIRED'), value: 'Retirado' }
    ];

    this.menuItems = [
      {
        label: t('MEMBER_DETAIL.MENU_PERSONAL'),
        icon: 'pi pi-person',
        command: () => this.activeView = 'personal'
      },
      {
        label: t('MEMBER_DETAIL.MENU_SPIRITUAL'),
        icon: 'pi pi-address-book',
        command: () => this.activeView = 'espiritual'
      },
      {
        label: t('MEMBER_DETAIL.MENU_HEALTH'),
        icon: 'pi pi-heart-fill',
        command: () => this.activeView = 'salud'
      },
      {
        label: t('MEMBER_DETAIL.MENU_PASTORAL'),
        icon: 'pi pi-bookmark-fill',
        command: () => this.activeView = 'pastorales'
      },
      {
        label: t('MEMBER_DETAIL.MENU_TITHES'),
        icon: 'pi pi-wallet',
        command: () => {
          this.activeView = 'diezmos';
          this.loadDiezmoSummaries();
        }
      }
    ];
  }

  private buildTipoDocumentoOptions(): void {
    this.tipoDocumentoOptions = this.rawTipoDocumentoOptions.map((o: any) => ({
      label: this.languageService.getItemLabel(o.value, o.label),
      value: o.value
    }));
  }

  ngOnDestroy(): void {
    if (this.catalogosSubscription) {
      this.catalogosSubscription.unsubscribe();
    }
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
    this.membersSubscription?.unsubscribe();
    this.familiesSubscription?.unsubscribe();
  }

}
